import { Injectable, isDevMode } from '@angular/core';
import { BehaviorSubject, firstValueFrom, forkJoin, Observable } from 'rxjs';
import { catchError, map, filter } from 'rxjs/operators';
import { IEidosMenuAction } from '@common/config/environment.interface';
import { EidosLogSeverity, CoreRoute, MenuActionType, LocalStorageKeys, SecurityCheckRequestType, EidosAuthType } from '@common/models/core-constant.model';
import { IEidosDashboardConfig } from '@common/models/eidos-dashboard.interface';
import { EidosLanguage } from '@common/models/eidos-language.model';
import { EidosMenu } from '@common/models/eidos-menu.model';
import { IEidosDataRequestOptions } from '@common/models/eidos-object.interface';
import { EidosDataRequest, EidosObjectApiResponse, EidosObjectDataApiResponse, EidosDataObjectPagination, EidosDataObjectFieldSort, EidosSortType } from '@common/models/eidos-object.model';
import { IEidosResetCredentialsRequest } from '@common/models/mybiz.interface';
import { MyBizApiResponse, MyBizLabels, MyBizVersionApiResponse } from '@common/models/mybiz.model';
import { EidosUser } from '@common/models/user-info.model';
import { EidosApiService } from './eidos-api.service';
import { EidosLogService } from './eidos-log.service';
import { EidosSecurityService } from './eidos-security.service';
import { CoreFormatService } from './core-format.service';
import { CoreCacheService } from './core-cache.service';
import { CoreModalService, ModalBaseActions, ModalTone } from './core-modal.service';
import { EidosCompany } from '../models/eidos-company.model';
import { EidosOrganization } from '../models/eidos-organization.model';
import { CoreTextModalComponent } from '../components/core-modals/core-text-modal/core-text-modal.component';
import { IDynamicApiResponse } from '../models/dynamic-api-response.model';
import { DateTime } from 'luxon';

export enum EidosUtilityDialogDialogType {
  Generic,
  Warning,
  Info,
  Debug,
  Alert,
  Fault,
  Error,
  Ask,
}
export interface IEidosUtilityDialogOptions {
  title?: string;
  dialogType?: EidosUtilityDialogDialogType;
  message: string;
  info?: string;
  iconSize?: number;
  error?: Array<string>;
  size?: string;
  captionOk?: string;
  captionCancel?: string;
}

type AnyObject = Record<string, unknown>;
type KeyStructure = Record<string, string>;

export class CoreUtilityDialog {

  constructor(
    private coreModalService: CoreModalService,
  ) {}

  public async error(data: IEidosUtilityDialogOptions, err?: IDynamicApiResponse): Promise<void> {
    let errorMsg = ''
    if (data?.error instanceof Array && data.error.length > 0) {
      errorMsg = data?.error.join('<br />');
    } else if (typeof data?.error === 'string') {
      errorMsg = data.error;
    }

    const msg = (data?.message ?? '') + '<br />' + errorMsg + (err && err.Errors ? '<br />' + err.Errors.map((e: any) => e.ErrorDesc) : '');

      await firstValueFrom(
        this.coreModalService.open<CoreTextModalComponent, string, boolean>(CoreTextModalComponent, {
          title: data.title ?? '',
          data: msg,
          tone: ModalTone.ERROR,
          hasActions: ModalBaseActions.CLOSE,
          dimensions: data.size ? { width: data.size } : { maxWidth: '560px' },
          closeOnEscOrBackdropClick: false,
        })
      );
  }
  public async info(data: IEidosUtilityDialogOptions): Promise<void> {
      await firstValueFrom(
        this.coreModalService.open<CoreTextModalComponent, string, boolean>(CoreTextModalComponent, {
          title: data.title ?? '',
          data: data.message,
          tone: ModalTone.INFO,
          hasActions: ModalBaseActions.CLOSE,
          dimensions: data.size ? { width: data.size } : { maxWidth: '560px' },
          closeOnEscOrBackdropClick: false,
        })
      );
  }
  public async alert(data: IEidosUtilityDialogOptions): Promise<void> {

      await firstValueFrom(
        this.coreModalService.open<CoreTextModalComponent, string, boolean>(CoreTextModalComponent, {
          title: data.title ?? '',
          data: data.message,
          tone: ModalTone.WARNING,
          hasActions: ModalBaseActions.CLOSE,
          dimensions: data.size ? { width: data.size } : { maxWidth: '560px' },
          closeOnEscOrBackdropClick: false,
        })
      );
  }
  public async confirm(data: IEidosUtilityDialogOptions): Promise<boolean> {
    const buttonClasses = ['eidos-button', 'eidos-button-outlined', 'eidos-button-big'];
      const result = await firstValueFrom(
        this.coreModalService.open<CoreTextModalComponent, string, boolean>(CoreTextModalComponent, {
          title: data.title ?? '',
          data: data.message,
          tone: ModalTone.CONFIRM,
          hasActions: ModalBaseActions.NONE,
          customActions: [
            { label: data.captionCancel ?? 'CANCEL', class: [...buttonClasses, 'eidos-button-cancel'], value: false },
            { label: data.captionOk ?? 'CONFIRM', class: [...buttonClasses, 'eidos-button-success'], value: true },
          ],
          dimensions: data.size ? { width: data.size } : { maxWidth: '560px' },
          closeOnEscOrBackdropClick: false,
        })
      );
      return result === true;
  }
}

@Injectable({
  providedIn: 'root'
})
export class EidosUtilityService {
  /**
   * Only admin actions
   *
   * @readonly
   * @private
   * @memberof EidosUtilityService
   */
  private readonly DEVELOPER_ACTIONS = [MenuActionType.Debug, MenuActionType.Readme];

  /**
   * Eidos labels from MyBiz
   *
   * @private
   * @type {MyBizLabels}
   * @memberof EidosUtilityService
   */
  private labels: MyBizLabels = new MyBizLabels();

  /**
   * Current debug mode
   *
   * @memberof EidosUtilityService
   */
  public inDebugMode = new BehaviorSubject<boolean>(false);


  private _skipNextError = false
  get skipNextError(): boolean {
    const value = this._skipNextError;
    this._skipNextError = false;
    return value
  }
  set skipNextError(value: boolean) {
    this._skipNextError = value
  }
  /**
   * Hide sidebar flag
   *
   * @type {BehaviorSubject<boolean>}
   * @memberof EidosUtilityService
   */
  public hideSidebar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public onPrepareEnvironment: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /**
   * Hide topbar flag
   *
   * @type {BehaviorSubject<boolean>}
   * @memberof EidosUtilityService
   */
  public hideTopbar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Current menu
   *
   * @memberof EidosUtilityService
   */
  public currentMenu: BehaviorSubject<EidosMenu>;

  /**
   * Current dashboard
   *
   * @memberof EidosUtilityService
   */
  public currentDashboard: BehaviorSubject<IEidosDashboardConfig | undefined>;

  /**
   * Current user actions
   *
   * @memberof EidosUtilityService
   */
  public currentActions: BehaviorSubject<Array<IEidosMenuAction>>;

  /**
   * Current user info
   *
   * @private
   * @type {(EidosUser | null)}
   * @memberof EidosUtilityService
   */
  private currentUser: EidosUser | null = null;
  /**
   * Default actions in profile menu
   *
   * @private
   * @type {Array<IEidosMenuAction>}
   * @memberof EidosUtilityService
   */
  private defaultMenuActions: Array<IEidosMenuAction> = [
    {
      id: MenuActionType.Readme,
      icon: 'fa-book',
      labelCod: 'readme',
      order: 100
    },
    {
      id: MenuActionType.Debug,
      icon: 'fab fa-android',
      labelCod: 'debug_mode',
      order: 100
    },
    {
      id: MenuActionType.Settings,
      icon: 'fa-gear',
      labelCod: 'settings',
      order: 50
    },
    {
      id: MenuActionType.ChangePassword,
      icon: 'fa-lock',
      labelCod: 'change_password',
      order: 50
    },
    {
      id: MenuActionType.Refresh,
      icon: 'fa-arrows-rotate',
      labelCod: 'cache',
      order: 10
    },
    {
      id: MenuActionType.Impersonate,
      icon: 'fa-users',
      labelCod: 'impersonate_user',
      isDisabled: () => !!this.currentUser?.isImpersonated,
      order: 100
    },
    {
      id: MenuActionType.StopImpersonate,
      icon: 'fa-user',
      labelCod: 'user_impersonated',
      isDisabled: () => !this.currentUser?.isImpersonated,
      order: 100
    },
    {
      id: MenuActionType.Logout,
      icon: 'fa-power-off',
      labelCod: 'logout',
      order: -100
    }
  ];

  utilityDialogs: CoreUtilityDialog;

  constructor(
    private eidosApi: EidosApiService,
    private coreModalService: CoreModalService,
    private eidosSecurityService: EidosSecurityService,
    private eidosLogService: EidosLogService,
    private coreCacheService: CoreCacheService
  ) {
    this.utilityDialogs = new CoreUtilityDialog(this.coreModalService);

    this.currentMenu = new BehaviorSubject<EidosMenu>(new EidosMenu());
    this.currentDashboard = new BehaviorSubject<IEidosDashboardConfig | undefined>(undefined);
    this.currentActions = new BehaviorSubject<Array<IEidosMenuAction>>([]);

    this.eidosSecurityService.currentLoggedUser
      .subscribe(user => {
        // Current user can be invalid
        this.currentUser = user;
        this.setupUtilities(user);
      });

    this.eidosSecurityService.authenticated
      .pipe(filter(isAuthenticated => isAuthenticated !== undefined))
      .subscribe(isAuthenticated => {
        if (!isAuthenticated) {
          // clearAuthentication already redirects; keep this as a fallback for
          // authenticated.next(false) without going through clearAuthentication
          this.eidosSecurityService.redirectToLoginUnlessExempt();
        }
      });
  }

  isOnByApiLevel(level:'localhost'|'test'|'uat'|'training'|'prod') {

    const parts = this.eidosApi.urlAPI.split('//')
    if(parts.length<2) return false

    const domain = parts[1].toLowerCase().replace('oneisadmin.','')
    switch(level) {
      case 'localhost':
        return domain.startsWith('localhost')
      case 'test':
        return domain.startsWith('test')
      case 'uat':
        return domain.startsWith('uat')
      case 'training':
        return domain.startsWith('training')
      case 'prod':
        return domain.startsWith('aktravelgroup') || domain.startsWith('app-oneis-prod-eastus-001-staging')
      default:
        return false
    }
  }
  /**
   * Setup utilities of service
   *
   * @private
   * @param {EidosUser} user
   * @memberof EidosUtilityService
   */
  private setupUtilities(user: EidosUser) {
    // If valid, setup utilities
    if (user.isValid) {

      this.setDebugMode(localStorage.getItem(LocalStorageKeys.DEBUG_MODE) === "true");

      this.setConfigActions();

      this.loadLabels();

      this.currentDashboard.next(user.dashboard);

      this.eidosApi.loadMenu().subscribe({
        next: (menu: EidosMenu) => {
          // this._currentMenu = menu;
          this.currentMenu.next(menu);
        },
        error: (error) => {
          this.eidosLogService.logDebug(EidosLogSeverity.Error, "EidosUtilityService::setupUtilities - eidosApi.loadMenu", error);
        },
        complete: () => { }
      });

    }
    else {
      // If user is invalid, set default utilities
      this.labels = new MyBizLabels();
      this.currentActions.next([]);
      this.currentDashboard.next({
        enabled: false,
        columns: 10,
        rows: 10,
        columnsGap: 0,
        rowsGap: 0,
        eidosObject: []
      });
      this.currentMenu.next(new EidosMenu());
      if (localStorage.getItem(LocalStorageKeys.DEBUG_MODE) === null) {
        this.setDebugMode(false);
      }
    }
  }

  /**
   * Load labels from APIs
   *
   * @memberof EidosUtilityService
   */
  public loadLabels(): void {
    this.eidosApi.getMyBizLabels().subscribe({
      next: (labels: MyBizLabels) => {
        this.labels = labels;
      },
      error: (error) => {
        this.labels = new MyBizLabels();
        this.eidosLogService.logDebug(EidosLogSeverity.Error, "EidosUtilityService::setupUtilities - eidosApi.loadLabels", error);
      },
      complete: () => { }
    });
  }

  /**
   * Filters and sets avalaible user actions basing on its permissions
   *
   * @private
   * @memberof EidosUtilityService
   */
  private setConfigActions(): void {

    let userActions = this.defaultMenuActions
      // If user authentication type is not Form, discard change password action
      .filter(action => this.currentUser?.authType === EidosAuthType.Form || (action.id !== MenuActionType.ChangePassword))
      // If user can not impersonate or is not impersonated, discard impersonate actions
      .filter(action => !!this.currentUser?.canImpersonate || !!this.currentUser?.isImpersonated || (action.id !== MenuActionType.Impersonate && action.id !== MenuActionType.StopImpersonate))
      // If user is not developer, discard developer actions
      .filter(action => !!this.currentUser?.isDeveloper || this.DEVELOPER_ACTIONS.every(actionId => actionId !== action.id));

    // userActions = userActions.map(action => {

    //   switch (action.id) {
    //     case MenuActionType.Impersonate:
    //       action.enabled =  !this.currentUser?.isImpersonated;
    //       break;
    //     case MenuActionType.StopImpersonate:
    //       action.enabled = !!this.currentUser?.isImpersonated;
    //       break;
    //     default:
    //       break;
    //   }
    //   return action;
    // })

    this.currentActions.next(userActions);
  }

  /**
   * Retrive label specified by key, or its default
   *
   * @param {string} key
   * @param {string} [defaultText]
   * @param {...Array<string>} placeholders
   * @return {*}  {string}
   * @memberof EidosUtilityService
   */
  public getLabel(key: string, defaultText?: string, ...placeholders: Array<string>): string {

    let result: string;
    defaultText = defaultText ?? '*default';

    if (key) {
      result = this.labels.get(key) ?? defaultText;

      if (result && placeholders && placeholders.length > 0) {
        placeholders.forEach((item, i) => {
          result = result.replace(`${(i + 1)}`, item);
        });
      }
    } else {
      result = defaultText;
    }

    return result;
  }

  /**
   * Gets MyBiz app version
   *
   * @return {*}  {Observable<EidosMyBizVersionApiResponse>}
   * @memberof EidosUtilityService
   */
  getMyBizVersion(): Observable<MyBizVersionApiResponse> {
    return this.eidosApi.getMyBizVersion();
  }
  /**
   * Gets a generic MyBiz object
   *
   * @param {number} objectId
   * @return {*}  {Observable<EidosObjectApiResponse>}
   * @memberof EidosUtilityService
   */
  getObject(objectId: number): Observable<EidosObjectApiResponse> {
    return this.eidosApi.getObject(objectId);
  }

  /**
   * Gets data of a generic MyBiz object
   *
   * @param {number} objectId
   * @param {number} page
   * @param {IEidosDataRequestOptions} [options]
   * @return {*}  {Observable<EidosObjectDataApiResponse>}
   * @memberof EidosUtilityService
   */
  getDataObject(objectId: number, page: number, options?: IEidosDataRequestOptions): Observable<EidosObjectDataApiResponse> {

    let requestOptions: EidosDataRequest = new EidosDataRequest();
    requestOptions.page = page;
    if (options) {
      if (options.pagination) {
        requestOptions.pageSize = (options?.pagination as EidosDataObjectPagination).pageSize
      }
      if (options.sort && options.sort.length > 0) {
        requestOptions.orderby = options.sort!.map((s: EidosDataObjectFieldSort) => {
          return {
            fieldName: s.fieldName,
            priority: s.priority,
            isAscending: s.type === EidosSortType.Ascending
          }
        });
      }
      if (options.filters && options.filters.length > 0) {
        requestOptions.filters = options.filters!;
      }
    }
    this.eidosLogService.logDebug(EidosLogSeverity.Log, 'getDataObject', objectId, requestOptions);
    return this.eidosApi.getDataObject(objectId, requestOptions);
  }

  /**
   * Set or toggle debug mode, if user is admin
   *
   * @param {boolean} [enable] Mode setting
   * @memberof EidosUtilityService
   */
  public setDebugMode(enable?: boolean): void {
    const canDebugMode = this.eidosSecurityService.isDeveloperUser();
    let nextState = enable !== undefined ? enable : (canDebugMode && !this.inDebugMode.value);
    if (isDevMode()) {
      nextState = true;
    }
    const storageState = nextState ? "true" : "false";
    localStorage.setItem(LocalStorageKeys.DEBUG_MODE, storageState);
    this.eidosLogService.inDebugMode = nextState;
    this.inDebugMode.next(nextState);
  }

  /**
   * Logout user from Eidos and redirect him to login
   *
   * @memberof EidosUtilityService
   */
  public logout(): void {
    this.eidosApi.logout()
      .subscribe({
        next: async (response) => {
          if (response.isOk()) {
            this.eidosSecurityService.clearAuthentication();
            this.eidosSecurityService.clearRequestedRoute();
            sessionStorage.clear();

            localStorage.clear();
            const allCookies = document.cookie.split(';');
            const oldDate = new Date(0).toUTCString()
            allCookies.forEach(c => {
              document.cookie = `${c}=;expires=${oldDate}`
            })

            await this.coreCacheService.eraseCache();
            window.location.href = window.location.origin + '/' + CoreRoute.Login;
            return true;
          } else {
            return false;
          }
        },
        error: () => false,
        complete: () => { },
      });
  }

  public getMyBizImageUrl(imageCod: string): Observable<string> {
    return this.eidosApi.getMyBizImageUrl(imageCod);
  }

  public changePassword(oldPassword: string, newPassword: string): Observable<string> {
    const response = this.eidosApi
      .changePassword(oldPassword, newPassword)
      .pipe(
        map<MyBizApiResponse, string>(response => {
          if (response.isOk()) {
            if (this.currentUser?.mustChangePassword) {
              this.updateUser({ must_change_password: false });
            }
            return "";
          } else {
            return response.error ?? "Error while changing password.";
          }
        }),
        catchError(async (err: string) => err)
      );
    return response;
  }

  /**
   * Requests a user credentials reset
   *
   * @param {string} username
   * @return {*}  {Observable<string>}
   * @memberof EidosUtilityService
   */
  public resetCredentials(username: string): Observable<string> {
    const payload: IEidosResetCredentialsRequest = CoreFormatService.IsEmail(username) ? { email: username } : { username: username };

    const response = this.eidosApi
      .resetCredentials(payload)
      .pipe(
        map<MyBizApiResponse, string>(response => {
          return response.isOk() ? response.data : "";
        }),
        catchError(async () => "")
      );
    return response;
  }
  /**
   * Confirms a user credentials reset
   *
   * @param {string} resetId
   * @return {*}  {Observable<string>}
   * @memberof EidosUtilityService
   */
  public confirmResetCredentials(resetId: string): Observable<string> {
    const response = this.eidosApi
      .confirmResetCredentials(resetId)
      .pipe(
        map<MyBizApiResponse, string>(response => {
          return response.isOk() ? response.data : "";
        }),
        catchError(async () => "")
      );
    return response;
  }

  private updateUser(propertyToUpdate: any) {
    // this.currentUser = this.securityService.UpdateUserProperty(propertyToUpdate);
    this.eidosSecurityService.updateUserProperty(propertyToUpdate);
  }

  impersonateStart(user: string): Observable<string> {
    return this.eidosApi.impersonate(user).pipe(
      map<MyBizApiResponse, string>(response => {
        if (response.isOk()) {
          this.reloadUser();
          return '';
        } else {
          return response.error != null ? response.error : 'Error while impersonating user';
        }
      }),
      catchError(error => { throw typeof error === 'string' ? error : 'Error while impersonating user' })
    );
  }

  impersonateEnd(): Observable<boolean> {
    const response = this.eidosApi
      .endImpersonate()
      .pipe(
        map<MyBizApiResponse, boolean>(response => {
          if (response.isOk()) {
            this.reloadUser();
            return true;
          } else {
            return false;
          }
        }),
        catchError(async (err) => {
          this.eidosLogService.logDebug(EidosLogSeverity.Log, err);
          return false;
        })
      );
    return response;
  }
  public getAvailableLanguages(): Observable<Array<EidosLanguage>> {
    return this.eidosApi.getLanguages();
  }
  public getAvailableCompanies(): Observable<Array<EidosCompany>> {
    return this.eidosApi.getCompanies();
  }
  public getAvailableOrganizations(): Observable<Array<EidosOrganization>> {
    return this.eidosApi.getOrganizations();
  }
  public setLanguage(code: string): Observable<boolean> {
    const response = this.eidosApi
      .setCurrentLanguage(code)
      .pipe(
        map<MyBizApiResponse, boolean>(response => response.isOk()),
        catchError(async () => false)
      );
    return response;
  }
  public setCompany(code: string): Observable<boolean> {
    const response = this.eidosApi
      .setCurrentCompany(code)
      .pipe(
        map<MyBizApiResponse, boolean>(response => response.isOk())
      );
    return response;
  }
  public setOrganization(code?: string): Observable<boolean> {
    const response = this.eidosApi
      .setCurrentOrganization(code)
      .pipe(
        map<MyBizApiResponse, boolean>(response => response.isOk()),
        catchError(async () => false)
      );
    return response;
  }
  //TODO modellizare setting
  public updateCurrentSetting(setting: any): Observable<boolean> {
    const response = forkJoin([
      this.setCompany(setting.companyCode),
      this.setLanguage(setting.languageCode),
      this.setOrganization(setting.organizationCode)
    ]).pipe(
      map<Array<boolean>, boolean>(([company, language]) => {
        this.loadLabels();
        this.reloadUser();
        return company && language;
      }),
      catchError(async () => false)
    );
    return response;
  }

  updateCurrentCompany(companyCode: string): Observable<boolean> {
    const response = this.setCompany(companyCode).pipe(
      map<boolean, boolean>(result => {
        this.loadLabels();
        this.reloadUser();
        return result;
      }),
      catchError(async (_err) => {
        return false
      })
    );
    return response;
  }

  public getCurrentUser(): EidosUser | null {
    return this.currentUser;
  }

  /**
   * Force reload user
   *
   * @memberof EidosUtilityService
   */
  private reloadUser(): void {
    this.eidosSecurityService.checkRequest.emit(SecurityCheckRequestType.User);
  }

  public cloneDeep(value: any, seen = new WeakMap()) {
    // Tipi primitivi e funzione (copiata per riferimento)
    if (typeof value !== 'object' || value === null) return value;

    // Evita duplicazioni su oggetti ricorsivi
    if (seen.has(value)) return seen.get(value);

    let result: any;

    const type = Object.prototype.toString.call(value);

    switch (type) {
      case '[object Date]':
        result = new Date(value.getTime());
        break;

      case '[object RegExp]':
        result = new RegExp(value.source, value.flags);
        break;

      case '[object Map]':
        result = new Map();
        seen.set(value, result);
        value.forEach((v: any, k: any) => {
          result.set(this.cloneDeep(k, seen), this.cloneDeep(v, seen));
        });
        break;

      case '[object Set]':
        result = new Set();
        seen.set(value, result);
        value.forEach((v: any) => {
          result.add(this.cloneDeep(v, seen));
        });
        break;

      case '[object ArrayBuffer]':
        result = value.slice(0);
        break;

      case '[object Uint8Array]':
      case '[object Int8Array]':
      case '[object Uint16Array]':
      case '[object Int16Array]':
      case '[object Uint32Array]':
      case '[object Int32Array]':
      case '[object Float32Array]':
      case '[object Float64Array]':
      case '[object BigInt64Array]':
      case '[object BigUint64Array]':
        result = new value.constructor(this.cloneDeep(value.buffer, seen), value.byteOffset, value.length);
        break;

      case '[object Array]':
        result = [];
        seen.set(value, result);
        value.forEach((item: any, i: any) => {
          result[i] = this.cloneDeep(item, seen);
        });
        break;

      case '[object Object]':
        // Preserva il prototipo
        result = Object.create(Object.getPrototypeOf(value));
        seen.set(value, result);
        for (const key in value) {
          if (Object.prototype.hasOwnProperty.call(value, key)) {
            result[key] = this.cloneDeep(value[key], seen);
          }
        }

        // Copia simboli
        const symbols = Object.getOwnPropertySymbols(value);
        for (const sym of symbols) {
          result[sym] = this.cloneDeep(value[sym], seen);
        }

        break;

      default:
        // Se non è un tipo gestito, ritorna per riferimento
        result = value;
        break;
    }

    seen.set(value, result);
    return result;
  }


  private normalizePrimitive(value: unknown): unknown {
    if (DateTime.isDateTime(value)) return value.toISODate();
    return value;
  }

  private normalizeValue(value: unknown): unknown {
    if (DateTime.isDateTime(value)) return value.toISODate();
    if (Array.isArray(value)) return value.map(this.normalizeValue);
    if (value && typeof value === 'object') {
      const sortedKeys = Object.keys(value as AnyObject).sort();
      const result: AnyObject = {};
      for (const key of sortedKeys) {
        result[key] = this.normalizeValue((value as AnyObject)[key]);
      }
      return result;
    }

    return value;
  }

  private deepEqual(a: unknown, b: unknown): boolean {
    return JSON.stringify(this.normalizeValue(a)) === JSON.stringify(this.normalizeValue(b));
  }

  deepDiff<T extends AnyObject>(original: T, modified: T, keyStructure?: KeyStructure): Partial<T> | undefined {
    const result: AnyObject = {};
    let hasChanges = false;

    const keys = new Set([
      ...Object.keys(original ?? {}),
      ...Object.keys(modified ?? {})
    ]);

    for (const key of keys) {
      const origVal = original?.[key];
      const modVal = modified?.[key];

      // Array with primary ID
      if (Array.isArray(modVal) && keyStructure && keyStructure[key]) {
        const idKey = keyStructure[key];
        const origArray = Array.isArray(origVal) ? origVal : [];

        const origMap = new Map<unknown, AnyObject>((origArray as AnyObject[]).map(item => [item[idKey], item]));
        const diffs: AnyObject[] = [];

        for (const item of modVal as AnyObject[]) {
          const itemId = item[idKey];
          const originalItem = origMap.get(itemId);

          if (!originalItem) { // New item
            diffs.push(item);
            continue;
          }

          if (item['status'] === 'D') { // Delete -> should send only id + status but it depends on BE (?)
            diffs.push(item);
            continue;
          }

          const itemDiff = this.deepDiff(originalItem, item, keyStructure);
          if (itemDiff) diffs.push(item);
        }

        if (diffs.length) {
          result[key] = diffs;
          hasChanges = true;
        }

        continue;
      }
      // Generic arrays (fallback when there's no ID)
      if (Array.isArray(modVal)) {
        if (!this.deepEqual(origVal, modVal)) {
          result[key] = modVal;
          hasChanges = true;
        }
        continue;
      }

      // DateTime
      if (DateTime.isDateTime(modVal) && DateTime.isDateTime(origVal)) {

        if (!(modVal.equals(origVal)) && (modVal.isValid || origVal.isValid)) {
          result[key] = modVal;
          hasChanges = true;
        }
        continue;
      }

      // Nested Objects
      if (modVal && typeof modVal === 'object') {
        const nestedDiff = this.deepDiff(
          (origVal ?? {}) as AnyObject,
          modVal as AnyObject,
          keyStructure
        );
        if (nestedDiff) {
          result[key] = nestedDiff;
          hasChanges = true;
        }
        continue;
      }

      // Primitives
      if (!this.deepEqual(
        this.normalizePrimitive(origVal),
        this.normalizePrimitive(modVal)
      )) {
        result[key] = modVal;
        hasChanges = true;
      }
    }

    return hasChanges ? result as Partial<T> : undefined;
  }
}
